import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { routePath } from '../../routes/routePath';
import Layout from '../../components/Layout';
import EvaluationNavigation from '../../components/EvaluationNavigation';
import ButtonComponent from '../../components/ButtonComponent';
import { getSubordinateToEvaluateAsync } from '../../services/evaluationService';
import MySpinner from '../../components/MySpinner';
import theme from '../../theme/theme';

const SubordinateEvaluation = () => {
  const navigate = useNavigate();
  const [evaluatedSubs, setEvaluatedSubs] = useState(null);
  const [unEvaluatedSubs, setUnEvaluatedSubs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSubordinates = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await getSubordinateToEvaluateAsync();
        const list = Array.isArray(result) ? result : [];
        setEvaluatedSubs(list.filter(s => s.isEvaluated));
        setUnEvaluatedSubs(list.filter(s => !s.isEvaluated));
      } catch (err) {
        if (err.response?.status === 404) {
          setError('لا توجد فترة تقييم نشطة حالياً');
        } else {
          setError(err.message || 'فشل في جلب البيانات');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchSubordinates();
  }, []);

 

  const handleEvaluation = (hierId) => {
    navigate(routePath.createNewEvaluation(hierId));
  };

  const updateEvaluation = (hierId) => {
    navigate(routePath.updateEvaluation(hierId));
  };

  if (loading) return <Layout><MySpinner color={theme.colors.primary} /></Layout>;
  
  if (error) return (
    <Layout>
      <EvaluationNavigation />
      <div className="alert alert-warning text-center my-5" style={{ maxWidth: '600px', margin: '2rem auto' }}>
        <strong></strong> {error}
      </div>
    </Layout>
  );

  return (
    <Layout>
      <EvaluationNavigation />
      
      <div style={styles.container}>
        {/* Evaluated Subordinates Section */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <span style={styles.badge}>✓</span>
              موظفين تم تقييمهم
            </h2>
            {evaluatedSubs && evaluatedSubs.length > 0 && (
              <span  style={styles.count}>{evaluatedSubs.length} موظف</span>
            )}
          </div>

          {evaluatedSubs && evaluatedSubs.length > 0 ? (
            <div style={styles.cardsGrid}>
              {evaluatedSubs.map((sub, index) => (
                <div key={sub.hierId} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <span style={styles.cardNumber}>#{index + 1}</span>
                    <span style={{ ...styles.statusBadge, ...styles.evaluatedBadge }}>
                      تم التقييم
                    </span>
                  </div>
                  
                  <div style={styles.cardBody}>
                    <h3 style={styles.empName}>{sub.empName}</h3>
                    <div style={styles.infoRow}>
                      <span style={styles.label}>القسم:</span>
                      <span style={styles.value}>{sub.deptName}</span>
                    </div>
                    <div style={styles.infoRow}>
                      <span style={styles.label}>المسمى:</span>
                      <span style={styles.value}>{sub.posTitle}</span>
                    </div>
                    <div style={styles.percentageBox}>
                      <span style={styles.percentageLabel}>النسبة الكلية</span>
                      <span style={styles.percentageValue}>{sub.percentage}%</span>
                    </div>
                  </div>

                  <div style={styles.cardFooter}>
                    <ButtonComponent
                      onClick={() => updateEvaluation(sub.hierId)}
                      text="تحديث التقييم"
                      className="primary-bg"
                      style={styles.updateButton}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={styles.emptyIcon}>✓</div>
              <p style={styles.emptyText}>لا يوجد موظفين تم تقييمهم</p>
            </div>
          )}
        </section>

        {/* Unevaluated Subordinates Section */}
        <section style={styles.section}>
          <div style={styles.sectionHeader}>
            <h2 style={styles.sectionTitle}>
              <span style={{ ...styles.badge, backgroundColor: '#ff9800' }}>⏳</span>
              موظفين بانتظار التقييم
            </h2>
            {unEvaluatedSubs && unEvaluatedSubs.length > 0 && (
              <span style={{ ...styles.count, backgroundColor: '#fff3e0', color: '#e65100' }}>
                {unEvaluatedSubs.length} موظف
              </span>
            )}
          </div>

          {unEvaluatedSubs && unEvaluatedSubs.length > 0 ? (
            <div style={styles.cardsGrid}>
              {unEvaluatedSubs.map((sub, index) => (
                <div key={sub.hierId} style={styles.card}>
                  <div style={styles.cardHeader}>
                    <span style={styles.cardNumber}>#{index + 1}</span>
                    <span style={{ ...styles.statusBadge, ...styles.pendingBadge }}>
                      بانتظار التقييم
                    </span>
                  </div>
                  
                  <div style={styles.cardBody}>
                    <h3 style={styles.empName}>{sub.empName}</h3>
                    <div style={styles.infoRow}>
                      <span style={styles.label}>القسم:</span>
                      <span style={styles.value}>{sub.deptName}</span>
                    </div>
                    <div style={styles.infoRow}>
                      <span style={styles.label}>المسمى:</span>
                      <span style={styles.value}>{sub.posTitle}</span>
                    </div>
                  </div>

                  <div style={styles.cardFooter}>
                    {sub.isEvaluationPeriodActive ? (
                      <ButtonComponent
                        onClick={() => handleEvaluation(sub.hierId)}
                        text="تقييم الموظف"
                        className="primary-bg"
                        style={styles.evaluateButton}
                      />
                    ) : (
                      <button
                        disabled
                        style={styles.disabledButton}
                        title="لا توجد فترة تقييم نشطة حالياً"
                      >
                        <span style={styles.disabledIcon}>🔒</span>
                        لا توجد فترة تقييم نشطة
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={styles.emptyState}>
              <div style={{ ...styles.emptyIcon, backgroundColor: '#fff3e0', color: '#ff9800' }}>⏳</div>
              <p style={styles.emptyText}>لا يوجد موظفين بانتظار التقييم</p>
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '2rem',
    direction: 'rtl'
  },
  section: {
    marginBottom: '3rem'
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem'
  },
  sectionTitle: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    margin: 0
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    borderRadius: '12px',
    backgroundColor: '#4caf50',
    color: 'white',
    fontSize: '1.2rem'
  },
  count: {
    padding: '0.5rem 1.25rem',
    borderRadius: '20px',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    fontWeight: 'bold',
    fontSize: '0.95rem'
  },
  cardsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
    gap: '1.5rem'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: '1px solid #e0e0e0',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    ':hover': {
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      transform: 'translateY(-2px)'
    }
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1rem 1.25rem',
    backgroundColor: '#f8f9fa',
    borderBottom: '1px solid #e0e0e0'
  },
  cardNumber: {
    fontSize: '0.9rem',
    fontWeight: 'bold',
    color: '#666'
  },
  statusBadge: {
    padding: '0.35rem 0.85rem',
    borderRadius: '12px',
    fontSize: '0.8rem',
    fontWeight: 'bold'
  },
  evaluatedBadge: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32'
  },
  pendingBadge: {
    backgroundColor: '#fff3e0',
    color: '#e65100'
  },
  cardBody: {
    padding: '1.5rem 1.25rem'
  },
  empName: {
    fontSize: '1.25rem',
    fontWeight: 'bold',
    color: '#1a1a1a',
    marginBottom: '1rem'
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.6rem 0',
    borderBottom: '1px solid #f0f0f0'
  },
  label: {
    fontSize: '0.9rem',
    color: '#666',
    fontWeight: '500'
  },
  value: {
    fontSize: '0.95rem',
    color: '#1a1a1a',
    fontWeight: '600'
  },
  percentageBox: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '1rem',
    padding: '1rem',
    backgroundColor: '#e3f2fd',
    borderRadius: '12px'
  },
  percentageLabel: {
    fontSize: '0.9rem',
    color: '#1565c0',
    fontWeight: '500'
  },
  percentageValue: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    color: '#0d47a1'
  },
  cardFooter: {
    padding: '1rem 1.25rem',
    backgroundColor: '#fafafa',
    borderTop: '1px solid #e0e0e0'
  },
  evaluateButton: {
    width: '100%'
  },
  updateButton: {
    width: '100%'
  },
  disabledButton: {
    width: '100%',
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    border: '2px dashed #ccc',
    backgroundColor: '#f5f5f5',
    color: '#999',
    fontSize: '0.95rem',
    fontWeight: 'bold',
    cursor: 'not-allowed',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    transition: 'all 0.2s ease'
  },
  disabledIcon: {
    fontSize: '1.1rem'
  },
  emptyState: {
    textAlign: 'center',
    padding: '4rem 2rem',
    backgroundColor: '#fafafa',
    borderRadius: '16px',
    border: '2px dashed #e0e0e0'
  },
  emptyIcon: {
    width: '80px',
    height: '80px',
    margin: '0 auto 1.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: '#e8f5e9',
    color: '#4caf50',
    fontSize: '2.5rem'
  },
  emptyText: {
    fontSize: '1.1rem',
    color: '#666',
    margin: 0
  }
};

export default SubordinateEvaluation;